/**
 * The ONE rule for "may the browser be sent here after a flow that proved who
 * you are".
 *
 * Every OAuth start route, every OAuth callback, the login page and the
 * Command Center's consent hand-off all carry a caller-supplied `next` /
 * `returnTo` path and eventually resolve it against this deployment's own
 * origin. Until this module existed the rule was written out ten times as
 * `value.startsWith("/") && !value.startsWith("//")`, and that is not a rule:
 *
 *   - `//evil.example/x` is a protocol-relative URL — refused by the second
 *     half, which is why the second half is there;
 *   - `/\evil.example/x` is the SAME URL in the spelling the WHATWG parser
 *     normalises (a backslash is a slash for special schemes), begins with one
 *     slash, and `new URL(value, origin)` resolves it to `https://evil.example/x`;
 *   - `/<TAB>/evil.example` is the same again — tabs and newlines are stripped
 *     before parsing, so the remainder starts with two slashes.
 *
 * Each of those turns an authenticated redirect into an off-origin landing:
 * the phishing primitive the check exists to prevent, and the one the OIDC
 * flow was fixed for while nine other callers kept the broken spelling.
 *
 * So the rule is a single leading slash, no backslash anywhere, no control
 * characters (tab and newline included), and a length bound. Anything else is
 * `null` and the caller lands the user on its own default — a login that ends
 * one page from where the user wanted is a nuisance; a login that ends on
 * someone else's host is an incident.
 *
 * Edge-compatible and dependency-free: `src/proxy.ts`'s neighbours and the
 * client login page both import it.
 */

/** Upper bound on a return path. Longer than any route this app has. */
const MAX_RETURN_TO_CHARS = 2048;

/**
 * True when the string carries a C0/DEL control character.
 *
 * Written as a character-code scan rather than a regex, for the reason
 * `integrations/gcp/logging.ts` states for the same check: a regex class over
 * control characters puts literal control BYTES in this source file, and a NUL
 * byte alone is enough to make the file binary to grep — so a later reader
 * searching for this function finds nothing.
 */
function hasControlCharacters(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return true;
  }
  return false;
}

/** The same-origin path this value may be redirected to, or `null`. */
export function safeReturnToPath(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (!raw || raw.length > MAX_RETURN_TO_CHARS) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//")) return null; // protocol-relative
  if (raw.includes("\\")) return null; // the same, normalised
  if (hasControlCharacters(raw)) return null;
  return raw;
}
